/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from '@angular/core';
import { DialogService } from 'nebula-ui-kit';
import { ConceptsModal } from '../../domain/concepts-modal.model';
import { ConceptsService } from '../../data-access/concepts-modal/concepts.service';
import { lastValueFrom, map, Observable } from 'rxjs';
import { SelectDialogComponent } from '../../../select-dialog/select-dialog.component';
import { TranslationService } from '@nebula-shared/data-access';

@Injectable({
  providedIn: 'root',
})
export class ConceptsModalService {
  readonly dialogService = inject(DialogService);
  readonly conceptosService = inject(ConceptsService);
  private readonly translation = inject(TranslationService);

  openSelector(
    title: string,
    currentValue?: string,
    soloActivos = true,
  ): Observable<ConceptsModal | null> {
    // El buscador de conceptos se pintaba con el texto en español fijo: el rotulo de sus dos
    // columnas y el del buscador salian igual en ingles (Mantis #323 punto 1). Se resuelven por
    // las claves que el resto de la aplicacion ya usa para lo mismo.
    const columns = [
      {
        key: 'codigo',
        label: this.translation.translate('COLUMNS.CODE'),
        sortable: true,
        width: '40%',
      },
      {
        key: 'nombre',
        label: this.translation.translate('COLUMNS.NAME'),
        sortable: true,
        width: '60%',
      },
    ];

    const loadDataFn = async (params: any): Promise<{ data: any[]; total: number }> => {
      try {
        const page = params.page - 1;
        const size = params.limit;
        const searchTerm = params.search || '';

        const response = await lastValueFrom(
          this.conceptosService.getConceptsPaginados(page, size, searchTerm, soloActivos, params),
        );

        return {
          data: response.content,
          total: response.totalElements,
        };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        return { data: [], total: 0 };
      }
    };

    const dialogRef = this.dialogService.open(SelectDialogComponent, {
      data: {
        title: title,
        columns: columns,
        multiSelect: false,
        searchPlaceholder: this.translation.translate('MODAL.SEARCH_BY_CODE_OR_NAME'),
        loadDataFn: loadDataFn,
        itemIdKey: 'id',
        selectedItems: [],
        displayFn: (item: any) => {
          const numeroDocumento = item.numeroDocumentoDisplay || item.numeroDocumento;
          const nombre = item.nombreDisplay || item.nombreCompleto || item.razonSocial;
          const nombreTruncado = this.truncateText(nombre, 30);
          return `${numeroDocumento} - ${nombreTruncado}`;
        },
      },
      size: 'lg',
      width: '800px',
    });

    return dialogRef.afterClosed().pipe(
      map((selected: ConceptsModal[]) => {
        return selected && selected.length > 0 ? selected[0] : null;
      }),
    );
  }

  truncateText(text: string, maxLength = 20): string {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }
}
